import { Api } from 'Utils/api';
import type { Campaign, CampaignFormData, CampaignRecipient, CampaignReply } from './types';
import type { PaginatedResponse } from '../shared/types';

const getHeaders = (ssoToken: string) => ({
  'X-GHL-SSO': ssoToken,
});

export const listCampaigns = async (
  companyId: string,
  ssoToken: string,
  page = 1,
): Promise<PaginatedResponse<Campaign>> => {
  const response = await Api.get(`/companies/${companyId}/ghl/campaigns`, {
    params: { page },
    headers: getHeaders(ssoToken),
  });
  return response.data;
};

export const getCampaign = async (
  companyId: string,
  campaignId: number,
  ssoToken: string,
): Promise<{ data: Campaign }> => {
  const response = await Api.get(`/companies/${companyId}/ghl/campaigns/${campaignId}`, {
    headers: getHeaders(ssoToken),
  });
  return response.data;
};

export const createCampaign = async (
  companyId: string,
  data: CampaignFormData,
  ssoToken: string,
): Promise<{ data: Campaign }> => {
  const response = await Api.post(`/companies/${companyId}/ghl/campaigns`, data, {
    headers: getHeaders(ssoToken),
  });
  return response.data;
};

export const updateCampaign = async (
  companyId: string,
  campaignId: number,
  data: Partial<CampaignFormData>,
  ssoToken: string,
): Promise<{ data: Campaign }> => {
  const response = await Api.put(`/companies/${companyId}/ghl/campaigns/${campaignId}`, data, {
    headers: getHeaders(ssoToken),
  });
  return response.data;
};

export const deleteCampaign = async (
  companyId: string,
  campaignId: number,
  ssoToken: string,
): Promise<void> => {
  await Api.delete(`/companies/${companyId}/ghl/campaigns/${campaignId}`, {
    headers: getHeaders(ssoToken),
  });
};

export const sendCampaign = async (
  companyId: string,
  campaignId: number,
  ssoToken: string,
): Promise<void> => {
  await Api.post(`/companies/${companyId}/ghl/campaigns/${campaignId}/send`, {}, {
    headers: getHeaders(ssoToken),
  });
};

export const cancelCampaign = async (
  companyId: string,
  campaignId: number,
  ssoToken: string,
): Promise<void> => {
  await Api.post(`/companies/${companyId}/ghl/campaigns/${campaignId}/cancel`, {}, {
    headers: getHeaders(ssoToken),
  });
};

export const listReplies = async (
  companyId: string,
  campaignId: number,
  ssoToken: string,
  page = 1,
): Promise<PaginatedResponse<CampaignReply>> => {
  const response = await Api.get(`/companies/${companyId}/ghl/campaigns/${campaignId}/replies`, {
    params: { page },
    headers: getHeaders(ssoToken),
  });
  return response.data;
};

export const listRecipients = async (
  companyId: string,
  campaignId: number,
  ssoToken: string,
  page = 1,
): Promise<PaginatedResponse<CampaignRecipient>> => {
  const response = await Api.get(`/companies/${companyId}/ghl/campaigns/${campaignId}/recipients`, {
    params: { page },
    headers: getHeaders(ssoToken),
  });
  return response.data;
};
